import { defineStore } from 'pinia';
import { differenceInDays, parseISO } from 'date-fns';
import Vue from 'vue';
import fallbackResponse from '@gms/api/src/api/fallbackCountriesApiResponse';
import { wrapStoreWithActivePinia } from '../composables/wrapStoreWithActivePinia';

const COUNTRIES = 'countries';
const DAYS_UNTIL_DIRTY = 90;

type CountryListHeaderItem = {header: string};
export type CountryListValueItem = {text: string, flagUrl: string, value: string};
export type CountryListItem = CountryListHeaderItem | CountryListValueItem;

export interface CountriesApiItem {
  cca2: string;
  continents: (
    'Africa'
    | 'Antarctica'
    | 'Asia'
    | 'Europe'
    | 'North America'
    | 'Oceania'
    | 'South America'
    )[]
  flags: {
    alt: string;
    png: string;
    svg: string;
  }
  name: {
    common: string;
    official: string;
  }
}

interface CountriesByContinent extends Record<string, undefined|CountriesApiItem[]>{
  Africa?: CountriesApiItem[],
  Antarctica?:CountriesApiItem[],
  Asia?: CountriesApiItem[],
  Europe?: CountriesApiItem[],
  'North America'?: CountriesApiItem[],
  Oceania?: CountriesApiItem[],
  'South America'?: CountriesApiItem[],
}

interface CountriesState {
  rawData: Array<CountriesApiItem>;
  lastFetch: Date | undefined;
  byContinents: CountriesByContinent | undefined
  loading: boolean;
}

const fallbackCountries = (fallbackResponse as CountriesApiItem[])

function mergeWithFallbackCountries(data: CountriesApiItem[] = []): CountriesApiItem[] {
  const countriesByCode = new Map<string, CountriesApiItem>();

  fallbackCountries.forEach((country) => countriesByCode.set(country.cca2, country));
  data.forEach((country) => countriesByCode.set(country.cca2, country));

  return Array.from(countriesByCode.values());
}

function translate(value: string): string {
  return Vue.prototype.$t?.(value)?.toString() ?? value;
}

function parseStoredCountries(data: string | null): {fetched?: string, data?: CountriesApiItem[]} {
  if (!data) {
    return {};
  }

  try {
    const parsedData = JSON.parse(data);
    if (!Array.isArray(parsedData?.data)) {
      return {};
    }

    return parsedData;
  } catch {
    return {};
  }
}

async function fetchCountriesData(): Promise<CountriesApiItem[]> {
  let jsonResult: CountriesApiItem[] | undefined;
  let response: Response | undefined;
  const fields = 'name,flags,continents,cca2';

  // Set a 10 seconds timeout for the request
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort();

    // When we abort the request due to the timeout, we check if the user already has country data.
    const storedData = window.localStorage.getItem(COUNTRIES);
    const dataObject = parseStoredCountries(storedData);

    // Set the previously stored country data as a fallback, or otherwise use the default fallback
    jsonResult = mergeWithFallbackCountries(dataObject.data);
  }, 10000);

  try {
    response = await fetch(
      `https://restcountries.com/v3.1/all?fields=${fields}`,
      { method: 'GET', signal: abortController.signal },
    )
      .then((r) => {
        clearTimeout(timeoutId);
        return r;
      });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // eslint-disable-next-line no-console
      console.warn('The request to the countries api was aborted due to timeout. A fallback result was used!')
    }
  } finally {
    clearTimeout(timeoutId);
  }

  if (jsonResult) {
    return jsonResult;
  }

  if (!response?.ok) {
    return mergeWithFallbackCountries(parseStoredCountries(window.localStorage.getItem(COUNTRIES)).data);
  }

  try {
    return mergeWithFallbackCountries(await response.json());
  } catch {
    return mergeWithFallbackCountries(parseStoredCountries(window.localStorage.getItem(COUNTRIES)).data);
  }
}

/**
 * Takes the given data, expected to be a Continents object, and sorts the individual lists.
 * @param data The continents data object
 * @returns The sorted continents data object
 */
function sortContinents(data: CountriesByContinent) {
  function compareCountriesByName(a: CountriesApiItem, b: CountriesApiItem) : number {
    return (a.name.common as string).localeCompare(b.name.common as string);
  }

  data.Africa?.sort(compareCountriesByName);
  data.Antarctica?.sort(compareCountriesByName);
  data.Asia?.sort(compareCountriesByName);
  data.Europe?.sort(compareCountriesByName);
  data['North America']?.sort(compareCountriesByName);
  data.Oceania?.sort(compareCountriesByName);
  data['South America']?.sort(compareCountriesByName);

  return data;
}

/**
 * Iterates over the raw countries data, and sorts them into the corresponding continent
 * @param data The raw countries data, as returned by the API call
 * @returns An object containing the continents as keys, and the country object arrays as values
 */
function parseDataByContinents(data: CountriesApiItem[]) {
  let result: CountriesByContinent = {
    Africa: [],
    Antarctica: [],
    Asia: [],
    Europe: [],
    'North America': [],
    Oceania: [],
    'South America': [],
  };

  data.forEach((element) => {
    const key = element.continents[0];
    if (!key) {
      return;
    }

    result[key]!.push(element);
  });

  return sortContinents(result);
}

/**
 * Checks, whether the data in the local storage should be updated. This depends on
 * whether there is actual data for the countries in the local storage, and if the data
 * is not older than DAYS_UNTIL_DIRTY days.
 * @param data The countries data from the local storage
 * @returns True, if the data should get updated. False otherwise
 */
function isDirty(data: string | null): boolean {
  const parsedData = parseStoredCountries(data);
  if (!parsedData.fetched) {
    return true;
  }

  const daysSinceFetch = Math.abs(differenceInDays(parseISO(parsedData.fetched), new Date()));
  return Number.isNaN(daysSinceFetch) || daysSinceFetch > DAYS_UNTIL_DIRTY;
}

// eslint-disable-next-line import/prefer-default-export
const _useCountriesStore = defineStore('countries', {
  state: (): CountriesState => {
    const countries = window.localStorage.getItem(COUNTRIES);
    const fallbackData = mergeWithFallbackCountries();

    const emptyState = {
      rawData: fallbackData,
      byContinents: parseDataByContinents(fallbackData),
      lastFetch: undefined,
      loading: false,
    };

    if (!countries) {
      return emptyState;
    }

    // We know that countries can't be null here, so cast it to clarify this for the interpreter
    const countriesObject = parseStoredCountries(countries);

    if (isDirty(countries)) {
      return emptyState;
    }

    const countryData = mergeWithFallbackCountries(countriesObject.data);

    return {
      rawData: countryData,
      byContinents: parseDataByContinents(countryData),
      lastFetch: parseISO(countriesObject.fetched as string),
      loading: false,
    };
  },
  getters: {
    available: (state): Array<CountryListItem> => {
      if (state.loading && !state.byContinents) {
        return [];
      }

      const list: Array<CountryListItem> = [];
      Object.keys(state.byContinents ?? {})
        .map((value) => ({ originalName: value, translatedName: translate(value) }))
        .sort((a, b) => `${a.translatedName}`.localeCompare(`${b.translatedName}`))
        .forEach((continent) => {
          list.push({ header: continent.translatedName });

          const sortedCountriesByContinent = [
            ...state.byContinents?.[continent.originalName] ?? [],
          ];
          list.push(
            ...(
              sortedCountriesByContinent
                .map((item) => ({
                  text: translate(item.name.common),
                  flagUrl: item.flags.svg,
                  value: item.cca2,
                }))
                // For some reason, sorting directly by mathematical operators does not work.
                .sort((a, b) => {
                  if (a.text < b.text) {
                    return -1;
                  }
                  if (a.text === b.text) {
                    return 0;
                  }
                  return 1;
                })
            ),
          );
        });

      return list;
    },
    nameFromCountryCode: (state) => (code: string): string => {
      const country = state.rawData.find((item) => item.cca2 === code);
      if (country) {
        return country.name.common;
      }

      return code;
    },
  },
  actions: {
    async getByCountryCode(countryCode: string) {
      await this.fetchData()
      const country = this.rawData.find((item) => item.cca2 === countryCode)
      if (!country) {
        return undefined;
      }

      return {
        text: translate(country.name.common),
        flagUrl: country.flags.svg,
        value: country.cca2,
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    async fetchData(forceUpdate = false, onSuccess = () => {}) {
      this.loading = true;
      const storedData = window.localStorage.getItem(COUNTRIES);

      if (!forceUpdate && !isDirty(storedData)) {
        const dataObject = parseStoredCountries(storedData);
        const data = mergeWithFallbackCountries(dataObject.data);
        this.rawData = data;
        this.byContinents = parseDataByContinents(data);
        this.lastFetch = parseISO(dataObject.fetched as string);

        // Call the provided callback
        onSuccess();
        this.loading = false;
        return;
      }

      const result = await fetchCountriesData()
      this.rawData = result;
      this.byContinents = parseDataByContinents(result);
      this.lastFetch = new Date();

      window.localStorage.setItem(
        COUNTRIES,
        JSON.stringify({
          fetched: this.lastFetch.toISOString(),
          data: result,
        }),
      );

      // Call the provided callback
      onSuccess();
      this.loading = false
    },
  },
})

export const useCountriesStore = wrapStoreWithActivePinia(_useCountriesStore);
